import DataTable from '../../../components/DataTable/DataTable';
import React, { useEffect, useMemo, useState } from 'react';
import ScheduleInfoBar from './scheduleInfoBar';
import UpdateScheduleModal from './updateScheduleModal';
import useSegmentRemodelConfig from '../../../graphql/hooks/useSegmentRemodelConfig';
import {
  DEFAULT_REMODELING_FREQUENCY,
  REMODELING_COLUMN_KEYS,
  getRemodelingColumns,
} from '../constants';
import {
  OTS_SGM_MNG_NO_REMODELING_RECORDS,
  OTS_SGM_MNG_REMODELING_SCHEDULE_OFF,
} from '../../../content';
import { RemodelingScheduleConfig } from '../types';
import {
  calculateNextRemodelDate,
  formatDisplayDate,
  formatScheduleLabel,
  sortRemodelingRecords,
  transformSegmentExports,
} from './utils';
import { renderRemodelingRowCells } from './renderers';
import * as Styled from './index.styles';

interface RemodelingProps {
  segmentId: string;
  segmentExports: any[];
  exportsLoading: boolean;
  exportsError: any;
}

const Remodeling: React.FC<RemodelingProps> = ({
  segmentId,
  segmentExports,
  exportsLoading: loading,
  exportsError: error,
}) => {
  // Fetch segment remodel configuration
  const {
    loading: configLoading,
    error: configError,
    config,
    refetch: refetchConfig,
  } = useSegmentRemodelConfig({
    segmentId,
  }) as any;

  // Transform API data to RemodelingRecord format
  const data = useMemo(() => {
    return transformSegmentExports(segmentExports);
  }, [segmentExports]);

  // Get last exported date from the most recent export
  const lastExportedDate = useMemo(() => {
    if (data.length > 0) {
      const sortedData = [...data].sort((a, b) => {
        const dateA = new Date(a.exportDate).getTime();
        const dateB = new Date(b.exportDate).getTime();
        return dateB - dateA;
      });
      return sortedData[0]?.exportDate || null;
    }
    return null;
  }, [data]);

  // Schedule configuration state - initialize from API config
  const [scheduleConfig, setScheduleConfig] =
    useState<RemodelingScheduleConfig>({
      enabled: false,
      frequency: DEFAULT_REMODELING_FREQUENCY,
      lastExportedDate: lastExportedDate,
    });

  // Update schedule config when API data is loaded
  useEffect(() => {
    if (config) {
      setScheduleConfig({
        enabled: config.enabled ?? false,
        frequency: config.remodel_interval || DEFAULT_REMODELING_FREQUENCY,
        lastExportedDate: lastExportedDate,
        optimizedAudienceId: config.optimized_audience_id ?? null,
        optimizedAudiences: config.optimized_audiences ?? [],
      });
    } else if (!configLoading && config === null) {
      setScheduleConfig({
        enabled: false,
        frequency: DEFAULT_REMODELING_FREQUENCY,
        lastExportedDate: lastExportedDate,
        optimizedAudienceId: null,
        optimizedAudiences: [],
      });
    } else if (lastExportedDate) {
      setScheduleConfig((prev) => ({
        ...prev,
        lastExportedDate: lastExportedDate,
      }));
    }
  }, [config, configLoading, lastExportedDate]);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortBy, setSortBy] = useState<string>(
    REMODELING_COLUMN_KEYS.EXPORT_DATE
  );
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');

  const columns = useMemo(() => {
    return getRemodelingColumns(sortBy, sortOrder);
  }, [sortBy, sortOrder]);

  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return sortRemodelingRecords(data, sortBy, sortOrder);
  }, [data, sortBy, sortOrder]);

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, page, rowsPerPage]);

  const rows = useMemo(() => {
    if (!paginatedData || paginatedData.length === 0) return [];
    return paginatedData.map((record) => renderRemodelingRowCells(record));
  }, [paginatedData]);

  // Handle sort change
  const handleSortChange = ({ column }: { column: string }) => {
    let newSortOrder: 'ASC' | 'DESC' = 'DESC';
    if (sortBy === column) {
      newSortOrder = sortOrder === 'ASC' ? 'DESC' : 'ASC';
    }
    setSortBy(column);
    setSortOrder(newSortOrder);
    setPage(0);
  };

  // Handle page change
  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (value: number) => {
    setRowsPerPage(value);
    setPage(0);
  };

  // Handle modal open/close
  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // Handle schedule config save
  const handleSaveSchedule = (newConfig: RemodelingScheduleConfig) => {
    setScheduleConfig(newConfig);
    // Refetch config after save to ensure UI is in sync
    refetchConfig();
  };

  // Calculate schedule display values
  const scheduleLabel = scheduleConfig.enabled
    ? formatScheduleLabel(scheduleConfig.frequency)
    : OTS_SGM_MNG_REMODELING_SCHEDULE_OFF;

  const lastExportedDisplay = formatDisplayDate(
    scheduleConfig.lastExportedDate
  );
  const nextExportDisplay = calculateNextRemodelDate(
    scheduleConfig.lastExportedDate,
    scheduleConfig.frequency,
    scheduleConfig.enabled
  );

  return (
    <>
      <ScheduleInfoBar
        schedule={scheduleLabel}
        lastExportedDate={lastExportedDisplay}
        nextExportDate={nextExportDisplay}
        onUpdateScheduleClick={handleOpenModal}
        loading={configLoading}
      />

      <Styled.ContentWrapper>
        <DataTable
          loading={loading || configLoading}
          error={!!error || !!configError}
          columns={columns}
          rows={(rows as any) || []}
          totalRows={sortedData.length}
          showRowsPerPageDropdown={true}
          customRowsPerPageOptions={[25, 50, 100]}
          defaultRowsPerPage={25}
          page={page}
          onPaginationClick={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          onSortChange={handleSortChange}
          noDataMessage={OTS_SGM_MNG_NO_REMODELING_RECORDS}
        />
      </Styled.ContentWrapper>

      {isModalOpen && (
        <UpdateScheduleModal
          segmentId={segmentId}
          config={scheduleConfig}
          lastExportedDate={lastExportedDisplay}
          nextExportDate={nextExportDisplay}
          onClose={handleCloseModal}
          onSave={handleSaveSchedule}
        />
      )}
    </>
  );
};

export default Remodeling;
